import {
  Medication,
  MedicationUpsellConfig,
} from "@types-runway/condition";
import { toSlug } from "@utils/content/toSlug";

export const isValidConfig = (payload: unknown): payload is MedicationUpsellConfig => {
  if (!payload || typeof payload !== "object") return false;
  const candidate = payload as Record<string, unknown>;
  return (
    typeof candidate.conditions === "object" &&
    candidate.conditions !== null &&
    !Array.isArray(candidate.conditions)
  );
};

export const allowsMultipleSelection = (
  config: MedicationUpsellConfig | null,
  conditionName: string
): boolean => {
  if (!config) return true;
  const conditionConfig = config.conditions[conditionName];
  if (!conditionConfig) return false;
  return conditionConfig.allowMultipleMedicationSelection;
};

// Support-use offerings are the $0 no-charge billing context of a medication,
// not a patient-facing alternative. The Stripe condition products list them
// alongside the real options, and the only marker the client receives is the
// product name. This filter is deliberately scoped to the no-config fallback:
// once the flag payload is available, promotedMedications is the authority.
const SUPPORT_USE_PATTERN = /support use/i;

export const isSupportMedication = (medication: Medication): boolean =>
  SUPPORT_USE_PATTERN.test(medication.name ?? "");

export const filterPromotedMedications = (
  config: MedicationUpsellConfig | null,
  conditionName: string,
  medications: Medication[]
): Medication[] => {
  if (!config) return medications.filter((med) => !isSupportMedication(med));
  const conditionConfig = config.conditions[conditionName];
  if (!conditionConfig) return medications;

  const promoted = conditionConfig.promotedMedications;
  if (!promoted || promoted.length === 0) return medications;

  const filtered = medications.filter((med) =>
    promoted.includes(med.id ?? "")
  );

  return filtered.length > 0 ? filtered : medications;
};

export const computeShowSeeOptions = (
  config: MedicationUpsellConfig | null,
  conditionName: string,
  medications: Medication[],
  isCartPreselected?: boolean,
  suggestedMedicationName?: string,
  suggestedMedicationId?: string
): boolean => {
  if (!allowsMultipleSelection(config, conditionName)) return false;
  if (shouldSuppressSeeOptions(
    config,
    conditionName,
    isCartPreselected ? suggestedMedicationName : undefined
  )) return false;

  const promoted = filterPromotedMedications(config, conditionName, medications);

  // If the preselected medication isn't in the promoted list, the patient
  // needs "See Options" to switch to a promoted one — even if only 1 remains.
  if (isCartPreselected && suggestedMedicationId) {
    const preselectedIsPromoted = promoted.some(
      (med) => med.id === suggestedMedicationId
    );
    if (!preselectedIsPromoted && promoted.length > 0) return true;
  }

  return promoted.length > 1;
};

interface ResolveDisplayedMedicationNameInput {
  medications: Medication[];
  suggestedMedicationName?: string;
  isCartPreselected?: boolean;
  hasMultipleMedications: boolean;
}

export const resolveDisplayedMedicationName = (
  input: ResolveDisplayedMedicationNameInput
): string | undefined => {
  if (input.isCartPreselected) return input.suggestedMedicationName;
  if (!input.hasMultipleMedications) return input.medications[0]?.name;
  return undefined;
};

export const shouldSuppressSeeOptions = (
  config: MedicationUpsellConfig | null,
  conditionName: string,
  preselectedMedicationName?: string
): boolean => {
  if (!config || !preselectedMedicationName) return false;
  const conditionConfig = config.conditions[conditionName];
  if (!conditionConfig) return false;
  const suppressList = conditionConfig.suppressSeeOptionsWhenCartPreselects;
  if (!Array.isArray(suppressList)) return false;
  return suppressList.some(
    (name) => toSlug(name) === toSlug(preselectedMedicationName)
  );
};
